import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    Modal,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useTheme,
    Card,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import formatCurrency from 'utils/formatCurrency';
import FormatDate from 'utils/formatDate';
import useBoolean from 'hooks/useBoolean';
import DashboardLayout from 'layouts/Dashboard/DashboardLayout';
import RoomTypeList from 'page-sections/room-type/RoomTypeList';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import downloadFile from 'utils/downloadFile';
import checkHotelInvoice from 'utils/checkHotelInvoice';
import PaidHotelInvoices from './sections/PaidHotelInvoice';

const HotelReservation = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [checkIn, setCheckIn] = useState(null);
    const [checkOut, setCheckOut] = useState(null);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const openSnackBar = useBoolean();
    const openDateModal = useBoolean();
    const isProcessing = useBoolean();
    const eligible = useBoolean();
    const [roomTypes, setRoomTypes] = useState([]);
    const [loadingRooms, setLoadingRooms] = useState(true);
    const [hotelInvoice, setHotelInvoice] = useState(null);
    const [paidHotelInvoices, setPaidHotelInvoices] = useState([]);
    const openModalPayment = useBoolean();

    useEffect(() => {
        const fetchRoomTypes = async () => {
            try {
                const res = await axiosInstance.get('/api/pub/room-types');
                setRoomTypes(res.data);
            } catch (err) {
                console.error('Failed to fetch room types:', err);
                setSnackbarMessage(
                    'Failed to load room types. Please try again.',
                );
                openSnackBar.onTrue();
            } finally {
                setLoadingRooms(false);
            }
        };
        fetchRoomTypes();
    }, []);

    useEffect(() => {
        const checkEligibility = async () => {
            try {
                const res = await axiosInstance.get(
                    '/api/prv/member-invoices',
                    {
                        params: {
                            packageType: 'main',
                        },
                    },
                );
                const hasPaid = res.data.some(
                    (invoice) => invoice.status.value === 'paid',
                );

                if (hasPaid) {
                    eligible.onTrue();
                } else {
                    eligible.onFalse();
                }
            } catch (err) {
                setSnackbarMessage(
                    'Error checking eligibility. Please try again.',
                );
                openSnackBar.onTrue();
            }
        };

        checkEligibility();
    }, [navigate]);

    useEffect(() => {
        const run = async () => {
            const { pendingHotelInvoice, paidHotelInvoice } = await checkHotelInvoice();
            setHotelInvoice(pendingHotelInvoice || null);
            setPaidHotelInvoices(paidHotelInvoice || []);
        };

        run();
    }, []);

    const validateDates = () => {
        if (!checkIn || !checkOut)
            return 'Please select check-in and check-out dates.';

        const nights = dayjs(checkOut).diff(dayjs(checkIn), 'day');

        if (
            dayjs(checkOut).isSame(dayjs(checkIn)) ||
            dayjs(checkOut).isBefore(dayjs(checkIn))
        )
            return 'Check-out must be after check-in.';
        if (nights < 1)
            return 'Reservations are limited to 1 consecutive nights.';

        if (
            dayjs(checkIn).isBefore(dayjs('2026-04-08'), 'day') ||
            dayjs(checkOut).isAfter(dayjs('2026-04-13'), 'day')
        )
            return 'Dates must be between April 8 and 13, 2026.';

        return null;
    };

    const handleReserve = async () => {
        const dateError = validateDates();

        if (dateError) {
            setSnackbarMessage(dateError);
            openSnackBar.onTrue();

            return;
        }

        if (!selectedRoom) {
            setSnackbarMessage('Please select a room type.');
            openSnackBar.onTrue();
            return;
        }

        isProcessing.onTrue();

        try {
            const availRes = await axiosInstance.post(
                `/api/prv/room-availabilities`,
                {
                    typeId: selectedRoom.id,
                    checkIn: FormatDate(dayjs(checkIn).toDate(), 'yyyy-MM-dd'),
                    checkOut: FormatDate(dayjs(checkOut).toDate(), 'yyyy-MM-dd'),
                },
            );

            if (!availRes.data.isAvailabel)
                throw new Error('No rooms available for the selected dates.');

            const nights = dayjs(checkOut).diff(dayjs(checkIn), 'day');
            const res = await axiosInstance.post('/api/prv/room-reservations', {
                typeId: selectedRoom.id,
                checkIn: FormatDate(dayjs(checkIn).toDate(), 'yyyy-MM-dd'),
                checkOut: FormatDate(dayjs(checkOut).toDate(), 'yyyy-MM-dd'),
            });

            const pricePerNight = parseInt(selectedRoom.price);
            const invoice = res.data;
            navigate('/dashboard/hotel-payment', {
                state: {
                    invoice,
                    hotel: {
                        id: selectedRoom.id,
                        name: selectedRoom.name,
                        price: pricePerNight * nights,
                        checkIn,
                        checkOut,
                        nights,
                    },
                },
            });
        } catch (err) {
            setSnackbarMessage(
                err.message || 'Reservation failed. Please try again.',
            );
            openSnackBar.onTrue();
        } finally {
            isProcessing.onFalse();
        }
    };

    const isConfirmDisabled =
        !selectedRoom ||
        !checkIn ||
        !checkOut ||
        !dayjs(checkOut).isAfter(dayjs(checkIn));

    return (
        <DashboardLayout>
            <Box pt={8} pb={4}>
                <Typography variant="h5" fontWeight={700} mb={1}>
                    Hotel Room Reservation
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={4}>
                    Reserve professional hotel rooms between
                    April 8 - 13, 2026. Eligible for users with paid package
                    invoices.
                </Typography>

                {!eligible.value && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{
                            mb: 4,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: theme.palette.warning.light,
                        }}
                    >
                        <Alert severity="warning" sx={{ flex: 1 }}>
                            Eligibility Required: You must complete a package
                            payment before reserving a hotel.
                        </Alert>
                        <Button
                            variant="contained"
                            onClick={() =>
                                navigate('/dashboard/choose-package')
                            }
                        >
                            Go to Choose Package
                        </Button>
                    </Stack>
                )}

                {eligible.value && hotelInvoice && (
                    <Card sx={{ p: 2, mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={700} mb={1}>
                            Pending Hotel Invoice
                        </Typography>
                        <Typography color="text.secondary">
                            You must pay or cancel the previous hotel invoice
                            before creating a new reservation.
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                onClick={() => openModalPayment.onTrue()}
                            >
                                Pay Now
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    downloadFile(
                                        `/api/prv/member-invoices/download/${hotelInvoice.id}`,
                                    )
                                }
                            >
                                Download Invoice
                            </Button>
                            <LoadingButton
                                variant="outlined"
                                color="error"
                                loading={isProcessing.value}
                                onClick={async () => {
                                    try {
                                        isProcessing.onTrue();
                                        await axiosInstance.patch(
                                            `/api/prv/member-invoices/${hotelInvoice.id}`,
                                            { status: 'cancel' },
                                        );
                                        setHotelInvoice(null);
                                    } finally {
                                        isProcessing.onFalse();
                                    }
                                }}
                            >
                                Cancel Invoice
                            </LoadingButton>
                        </Stack>
                    </Card>
                )}

                {paidHotelInvoices.length > 0 && (
                    <PaidHotelInvoices
                        paidHotelInvoices={paidHotelInvoices}
                    />
                )}

                <Modal
                    open={openDateModal.value}
                    onClose={openDateModal.onFalse}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 400 },
                            bgcolor: 'background.paper',
                            p: 4,
                            borderRadius: 3,
                        }}
                    >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack spacing={2}>
                                {selectedRoom && (
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ mb: 1 }}
                                    >
                                        Room: {selectedRoom.name}
                                    </Typography>
                                )}
                                <DatePicker
                                    label="Check-in Date"
                                    value={checkIn}
                                    onChange={(newValue) =>
                                        setCheckIn(newValue)
                                    }
                                    minDate={dayjs('2026-04-08')}
                                    maxDate={dayjs('2026-04-12')}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth />
                                    )}
                                />
                                <DatePicker
                                    label="Check-out Date"
                                    value={checkOut}
                                    onChange={(newValue) =>
                                        setCheckOut(newValue)
                                    }
                                    minDate={
                                        checkIn
                                            ? dayjs(checkIn).add(1, 'day')
                                            : dayjs('2026-04-08')
                                    }
                                    maxDate={dayjs('2026-04-13')}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth />
                                    )}
                                    disabled={!checkIn}
                                />
                                {selectedRoom && checkIn && checkOut && (
                                    <Box
                                        sx={{
                                            mt: 1,
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: 'action.hover',
                                        }}
                                    >
                                        <Stack spacing={0.5}>
                                            <Typography>
                                                Check-in:{' '}
                                                {FormatDate(
                                                    dayjs(checkIn).toDate(),
                                                    'dd MMM yyyy',
                                                )}
                                            </Typography>
                                            <Typography>
                                                Check-out:{' '}
                                                {FormatDate(
                                                    dayjs(checkOut).toDate(),
                                                    'dd MMM yyyy',
                                                )}
                                            </Typography>
                                            <Typography>
                                                Nights:{' '}
                                                {dayjs(checkOut).diff(
                                                    dayjs(checkIn),
                                                    'day',
                                                )}
                                            </Typography>
                                            <Typography fontWeight={700}>
                                                Total:{' '}
                                                {formatCurrency(
                                                    parseInt(
                                                        selectedRoom.price,
                                                    ) *
                                                        dayjs(checkOut).diff(
                                                            dayjs(checkIn),
                                                            'day',
                                                        ),
                                                )}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                )}
                            </Stack>
                        </LocalizationProvider>

                        <LoadingButton
                            variant="contained"
                            fullWidth
                            loading={isProcessing.value}
                            onClick={handleReserve}
                            sx={{ mt: 3 }}
                            disabled={isConfirmDisabled}
                        >
                            Confirm Reservation
                        </LoadingButton>
                    </Box>
                </Modal>

                <Snackbar
                    open={openSnackBar.value}
                    autoHideDuration={3000}
                    onClose={openSnackBar.onFalse}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity="error" onClose={openSnackBar.onFalse}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>

                <RoomTypeList
                    roomTypes={roomTypes}
                    loadingRooms={loadingRooms}
                    selectable={eligible.value}
                    disableSelect={!eligible.value || Boolean(hotelInvoice)}
                    selectedId={selectedRoom?.id}
                    onSelect={(room) => {
                        setSelectedRoom(room);
                        openDateModal.onTrue();
                    }}
                />

                <Dialog
                    fullScreen
                    open={openModalPayment.value}
                    onClose={openModalPayment.onFalse}
                >
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        Online Payment
                        <Button
                            onClick={openModalPayment.onFalse}
                            variant="outlined"
                        >
                            Back
                        </Button>
                    </DialogTitle>
                    <DialogContent>
                        {hotelInvoice?.invoiceUrl && (
                            <iframe
                                src={hotelInvoice.invoiceUrl}
                                style={{ border: 0, height: '100vh' }}
                                width="100%"
                                title="hotel-invoice"
                            ></iframe>
                        )}
                    </DialogContent>
                </Dialog>
            </Box>
        </DashboardLayout>
    );
};

export default HotelReservation;
